import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Plus, Pencil, BarChart3, Copy, Trash2, Rocket, Archive, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { DataTable, type ColumnDef } from '@/components/common/DataTable'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { formatDate, getApiErrorMessage } from '@/lib/utils'
import {
  useLandings,
  useCreateLanding,
  useDeleteLanding,
  usePublishLanding,
  useArchiveLanding,
  landingPublicUrl,
} from '@/hooks/useLandingPages'
import type { LandingListItem } from '@/types'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'default'> = {
  published: 'success',
  draft: 'warning',
  archived: 'default',
}
const STATUS_LABEL: Record<string, string> = {
  published: 'Publicada',
  draft: 'Borrador',
  archived: 'Archivada',
}

export const LandingPagesListPage = () => {
  const navigate = useNavigate()
  const [status, setStatus] = useState<string>('')
  const { data, isLoading } = useLandings({ status: status || undefined })
  const createLanding = useCreateLanding()
  const deleteLanding = useDeleteLanding()
  const publishLanding = usePublishLanding()
  const archiveLanding = useArchiveLanding()

  const [createOpen, setCreateOpen] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<LandingListItem | null>(null)

  const handleCreate = async () => {
    if (!newTitle.trim()) return
    try {
      const created = await createLanding.mutateAsync({ title: newTitle.trim() })
      setCreateOpen(false)
      setNewTitle('')
      navigate({ to: '/landing-pages/$pageId', params: { pageId: created.id } })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al crear landing'))
    }
  }

  const copyUrl = (l: LandingListItem) => {
    navigator.clipboard?.writeText(landingPublicUrl(l.slug, l.public_url))
    toast.success('URL copiada')
  }

  const columns: ColumnDef<LandingListItem>[] = [
    {
      key: 'title',
      header: 'Landing',
      cell: (l: LandingListItem) => (
        <Link
          to="/landing-pages/$pageId"
          params={{ pageId: l.id }}
          className="block hover:underline"
        >
          <div className="font-medium">{l.title}</div>
          <div className="font-mono text-xs text-muted-foreground">/l/{l.slug}</div>
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (l: LandingListItem) => (
        <Badge variant={STATUS_VARIANT[l.status] ?? 'default'}>
          {STATUS_LABEL[l.status] ?? l.status}
        </Badge>
      ),
    },
    {
      key: 'views',
      header: 'Vistas',
      cell: (l: LandingListItem) => <span className="font-medium">{l.views}</span>,
    },
    {
      key: 'conversions',
      header: 'Conversiones',
      cell: (l: LandingListItem) => <span className="font-medium">{l.conversions}</span>,
    },
    {
      key: 'updated_at',
      header: 'Actualizada',
      cell: (l: LandingListItem) => (
        <span className="text-sm text-muted-foreground">
          {l.updated_at ? formatDate(l.updated_at) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (l: LandingListItem) => (
        <div className="flex items-center justify-end gap-1">
          {l.status === 'published' && (
            <Button variant="ghost" size="sm" asChild title="Abrir">
              <a href={landingPublicUrl(l.slug, l.public_url)} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => copyUrl(l)} title="Copiar URL">
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" asChild title="Editar">
            <Link to="/landing-pages/$pageId" params={{ pageId: l.id }}>
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="ghost" size="sm" asChild title="Analítica">
            <Link to="/landing-pages/$pageId/analytics" params={{ pageId: l.id }}>
              <BarChart3 className="h-4 w-4" />
            </Link>
          </Button>
          {l.status !== 'published' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => publishLanding.mutate(l.id)}
              title="Publicar"
            >
              <Rocket className="h-4 w-4 text-primary" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => archiveLanding.mutate(l.id)}
              title="Archivar"
            >
              <Archive className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(l)} title="Eliminar">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Landing Pages</h1>
          <p className="text-muted-foreground">Crea, publica y mide landings para campañas.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Crear landing
        </Button>
      </div>

      <Tabs value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
        <TabsList>
          <TabsTrigger value="all">Todas</TabsTrigger>
          <TabsTrigger value="draft">Borradores</TabsTrigger>
          <TabsTrigger value="published">Publicadas</TabsTrigger>
          <TabsTrigger value="archived">Archivadas</TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        keyExtractor={(l: LandingListItem) => l.id}
        emptyMessage="No hay landings todavía"
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva landing</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label>Título</Label>
            <Input
              autoFocus
              placeholder="Ej. Campaña Instagram Mayo"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <p className="text-xs text-muted-foreground">
              Se crea como borrador con bloques de ejemplo. Podrás editarla y publicarla.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createLanding.isPending || !newTitle.trim()}>
              {createLanding.isPending ? 'Creando…' : 'Crear y editar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
          title="Eliminar landing"
          description={`¿Eliminar "${deleteTarget.title}"? Se borrarán también sus eventos. Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => deleteLanding.mutateAsync(deleteTarget.id)}
        />
      )}
    </div>
  )
}

export default LandingPagesListPage
